/**
 * Lightweight client-side cart persisted in localStorage.
 * One cart per registered client (keyed by client_id) so different
 * users on the same browser don't conflict.
 *
 * Cart shape:
 *   {
 *     [product_id]: {
 *       product_id, product_name, product_sku, unit, iva_percentage,
 *       price_unit,         // resolved client net unit price (server-side)
 *       price_list,         // base list price for crossed-out display
 *       quantity, qty_omaggio, note,
 *       sc1, sc2, sc3, sc4, // resolved discounts (read-only on client side)
 *     }
 *   }
 */

const KEY_PREFIX = "triolo_client_cart_";

const storageKey = (clientId) => `${KEY_PREFIX}${clientId || "anon"}`;

export function loadCart(clientId) {
  if (!clientId) return {};
  try {
    const raw = localStorage.getItem(storageKey(clientId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (err) {
    console.warn("loadCart: failed to parse stored cart", err);
    return {};
  }
}

export function saveCart(clientId, cart) {
  if (!clientId) return;
  try {
    localStorage.setItem(storageKey(clientId), JSON.stringify(cart || {}));
  } catch (err) {
    console.warn("saveCart: localStorage write failed (quota?)", err);
  }
}

export function clearCart(clientId) {
  if (!clientId) return;
  try {
    localStorage.removeItem(storageKey(clientId));
  } catch (err) {
    console.warn("clearCart: localStorage remove failed", err);
  }
}

/** Total number of selected products (lines, not item quantities). */
export function cartLineCount(cart) {
  return Object.values(cart || {}).filter(
    (it) => (Number(it?.quantity) || 0) > 0 || (Number(it?.qty_omaggio) || 0) > 0
  ).length;
}

/** Net total of the cart applying line discounts (sconto canale handled server-side at submit). */
export function cartSubtotal(cart) {
  let sum = 0;
  for (const it of Object.values(cart || {})) {
    const qty = Number(it?.quantity) || 0;
    const price = Number(it?.price_unit) || 0;
    let row = qty * price;
    for (const s of [it?.sc1, it?.sc2, it?.sc3, it?.sc4]) {
      const v = Number(s) || 0;
      if (v > 0) row *= 1 - v / 100;
    }
    sum += row;
  }
  return Math.round(sum * 100) / 100;
}
